/**
 * The check-in works offline and never blocks the shift (LAUNCH-PROPOSALS item 6), like the phone's
 * apps/mobile/src/logic/checkin.ts. A check-in saved on this device (lib/outbox.tsx) stands in for the server's until it
 * sends: an all-clear opens Do marked "sending…"; a not-OK never unlocks food work. Only the "check in first" block is
 * lifted: lessons and approval stay the server's (shiftGate). When it sends, the server's answer wins.
 */
import { checkinActionsFor, checkinAction, type DailyCheckin, type UsState } from "@logwave/core";
import type { Gate } from "../api.ts";
import type { CheckinSent, OutboxJob } from "./outbox.tsx";

export interface QueuedCheckin { localOk: boolean; health: DailyCheckin["health"]; handsWashed: boolean; at: string }

/** The newest check-in waiting on this device for this location and day (the outbox is already per person). */
export function queuedCheckin(pending: readonly OutboxJob[], locationId: string, date: string): QueuedCheckin | undefined {
  let found: (QueuedCheckin & { t: number }) | undefined;
  for (const j of pending) {
    const p = j.payload;
    if (p.kind !== "checkin" || p.body.locationId !== locationId || p.date !== date) continue;
    if (!found || j.createdAt >= found.t) found = { localOk: p.localOk, health: p.body.health, handsWashed: p.body.handsWashed, at: new Date(j.createdAt).toISOString(), t: j.createdAt };
  }
  if (!found) return undefined;
  const { t: _t, ...q } = found;
  return q;
}

/** Today's `me` as Do and the result see it while a check-in waits. `sending` marks it. */
export function withQueuedCheckin<M extends { gate: Gate; checkin?: DailyCheckin | null }>(me: M, q: QueuedCheckin | undefined, where: { orgId: string; locationId: string; employeeId?: string; date: string; state: UsState }): M & { sending: boolean } {
  if (!q) return { ...me, sending: false };
  const rest = me.gate.blocking.filter((b) => b.kind !== "not_checked_in");
  const block = { kind: "not_checked_in" as const, title: "no food work today", detail: checkinActionsFor(where.state)[checkinAction(q.health)].personInCharge, requirementIds: ["fc-employee-health"] };
  const gate: Gate = q.localOk ? { ...me.gate, ready: rest.length === 0, blocking: rest } : { ...me.gate, ready: false, blocking: [block, ...rest] as Gate["blocking"] };
  const checkin: DailyCheckin = {
    id: `${where.employeeId ?? "me"}_${where.date}`, orgId: where.orgId, locationId: where.locationId, employeeId: where.employeeId ?? "", date: where.date, at: q.at,
    health: q.health, handsWashed: q.handsWashed, okToWork: q.localOk,
  } as DailyCheckin;
  return { ...me, gate, checkin, sending: true };
}

export const CHECKIN_SENDING = {
  title: "Check-in sending…",
  ok: "You can start. It sends by itself when you're back online, and the server's answer counts.",
  notOk: "Saved on this device. It sends by itself when you're back online.",
};

/** After a queued check-in sent: undefined when the server agreed; otherwise what to say (the server wins). */
export function checkinSentNotice(sent: CheckinSent | undefined, state: UsState): { tone: "go" | "stop"; title: string; detail: string } | undefined {
  if (!sent || sent.localOk === sent.checkin.okToWork) return undefined;
  const a = checkinActionsFor(state)[checkinAction(sent.checkin.health)];
  if (sent.checkin.okToWork) return { tone: "go", title: "Your check-in was sent. You're cleared for food work.", detail: "The server's answer counts." };
  const why = a.foodWork ? "Wash your hands, then do the check-in again." : a.worker;
  return { tone: "stop", title: "Your check-in was sent. Stop food work for now.", detail: `The server says no food work yet. ${why}` };
}
